import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AuthShell } from "./Login";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, ShieldCheck, ShieldAlert } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [state, setState] = useState({ loading: true, valid: false, email: null });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!token) { setState({ loading: false, valid: false }); return; }
    api.get(`/auth/reset-password/verify?token=${encodeURIComponent(token)}`)
      .then(({ data }) => setState({ loading: false, valid: data.valid, email: data.email }))
      .catch(() => setState({ loading: false, valid: false }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) { setErr("Password must be at least 6 characters"); return; }
    if (password !== confirm) { setErr("Passwords don't match"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      localStorage.setItem("ss_token", data.token);
      await refresh();
      toast.success("Password reset — welcome back");
      navigate("/app", { replace: true });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail, "Reset failed"));
    } finally { setBusy(false); }
  };

  if (state.loading) {
    return (
      <AuthShell>
        <div className="overline mb-2">SITESYNC / RESET</div>
        <div className="text-sm text-muted-foreground font-mono animate-pulse">Verifying token…</div>
      </AuthShell>
    );
  }

  if (!state.valid) {
    return (
      <AuthShell>
        <div className="overline mb-2">SITESYNC / RESET</div>
        <h1 className="text-3xl font-semibold tracking-tighter mb-6">Link expired or invalid.</h1>
        <div className="border border-destructive/40 bg-destructive/5 rounded-sm p-5 flex items-start gap-3">
          <ShieldAlert className="h-5 w-5 text-destructive mt-0.5" />
          <div className="text-sm">
            This reset link is no longer valid. Links expire after 30 minutes and can only be used once.
          </div>
        </div>
        <div className="mt-6 flex gap-2">
          <Link to="/forgot-password"><Button className="rounded-sm bg-primary hover:bg-primary/90" data-testid="reset-request-new">Request new link</Button></Link>
          <Link to="/login"><Button variant="outline" className="rounded-sm border-border">Back to login</Button></Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div className="overline mb-2">SITESYNC / SET NEW PASSWORD</div>
      <h1 className="text-3xl font-semibold tracking-tighter mb-6">Choose a new password.</h1>
      {state.email && (
        <div className="text-sm text-muted-foreground mb-6 flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-primary" />
          <span>For <strong className="font-mono text-foreground">{state.email}</strong></span>
        </div>
      )}
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <div>
          <Label className="overline mb-2 block">New password</Label>
          <Input
            type="password"
            required
            minLength={6}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            className="rounded-sm font-mono"
            data-testid="reset-password-input"
          />
        </div>
        <div>
          <Label className="overline mb-2 block">Confirm new password</Label>
          <Input
            type="password"
            required
            minLength={6}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            className="rounded-sm font-mono"
            data-testid="reset-confirm-input"
          />
        </div>
        {err && (
          <div className="text-xs text-destructive font-mono border border-destructive/40 bg-destructive/5 px-3 py-2 rounded-sm" data-testid="reset-error">
            {err}
          </div>
        )}
        <Button type="submit" disabled={busy} className="w-full rounded-sm h-11 bg-primary hover:bg-primary/90" data-testid="reset-submit-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Set new password & sign in"}
        </Button>
      </form>
    </AuthShell>
  );
}
